import type { SerializedHardwarePackage } from '@enmon/commons/lib/tools';
import { Col, Input, Row } from 'antd';
import { Button, Form, InputNumber, Modal, requiredTextRule } from 'components';
import { HardwaresSelect } from 'components/form/select/HardwaresSelect';
import type { VFC } from 'react';
import { useUserInfo } from '@/hooks/useUser';
import { useCreateHardwarePackage } from 'api/hooks/hardware-package/useCreate';
import { useUpdateHardwarePackage } from 'api/hooks/hardware-package/useUpdate';
import { HW_PACKAGE_FORM_ID } from '../const';
import type { HardwarePackageFormValues } from '../types/HWPackagesFormValues';
import { Icon } from '@/components/icon';

interface HardwarePackagesModalProps {
  hardwarePackage?: SerializedHardwarePackage;
  onClose: () => void;
  onSuccess: (data: SerializedHardwarePackage) => void;
}

export const HardwarePackageModal: VFC<HardwarePackagesModalProps> = ({ hardwarePackage, onClose, onSuccess }) => {
  const { id } = hardwarePackage ?? {};
  const { reseller } = useUserInfo();
  const { mutate: updateHardwarePackage, isLoading: isUpdating } = useUpdateHardwarePackage({ onSuccess });
  const { mutate: createHardwarePackage, isLoading: isCreating } = useCreateHardwarePackage({ onSuccess });

  const handleOk = (values: HardwarePackageFormValues) => {
    return id ? updateHardwarePackage({ id, values }) : createHardwarePackage({ reseller, ...values });
  };

  const items = hardwarePackage?.hardwares ? hardwarePackage?.hardwares.map(item => item.id) : [];

  return (
    <Modal
      centered
      open
      cancelButtonProps={{ onClick: onClose }}
      confirmLoading={isCreating || isUpdating}
      maskClosable={false}
      okButtonProps={{
        htmlType: 'submit',
        form: HW_PACKAGE_FORM_ID,
      }}
      title={`${id ? 'Edit' : 'Add new'} hardware package`}
      onCancel={onClose}
    >
      <Form
        id={HW_PACKAGE_FORM_ID}
        initialValues={{ ...hardwarePackage, hardwares: items }}
        layout="vertical"
        onFinish={handleOk}
      >
        <Form.Item
          label="Name of hardware package"
          name={'name' satisfies keyof SerializedHardwarePackage}
          rules={[requiredTextRule]}
        >
          <Input placeholder="Acrios Puls LoRa" />
        </Form.Item>
        <Form.Item label="Priority" name={'priority' satisfies keyof SerializedHardwarePackage}>
          <InputNumber placeholder="2" />
        </Form.Item>
        <Form.List name={'hardwares' satisfies keyof SerializedHardwarePackage}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }, index) => (
                <Row key={key} align="top" gutter={[16, 16]}>
                  <Col span="22">
                    <Form.Item key={key} label={`Item ${index + 1}`} name={[name]} rules={[{ required: true }]}>
                      <HardwaresSelect useSimpleHardwareData />
                    </Form.Item>
                  </Col>
                  <Col span="2">
                    {/* Row with labels need extra space because of the correct alignment of delete button */}
                    <div style={{ height: '30px' }} />
                    <Button danger icon={<Icon iconString="delete" />} type="text" onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Button icon={<Icon iconString="plus" />} type="default" onClick={() => add()}>
                Add new HW
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};
